import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useEffect } from 'react';
import { BASE_URL } from '../../../utils/Constants';
import { useAuth } from '../../../utils/context/AuthContext';
import { useChatNotificationsStore } from '../../../utils/stores/useChatNotificationsStore';
import { ChatSession } from '../types/ChatSession';
import { hasUnreadMessages } from '../utils/unreadDetection';

async function getChatSessions(userId: string, userToken: string) {
  const response = await fetch(
    `${BASE_URL}/api/chat/expert/${userId}/sessions`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: userToken,
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch chat sessions');
  }

  return response.json() as Promise<ChatSession[]>;
}

const useGetChatSessions = () => {
  const { auth } = useAuth();
  const userId = auth.userId;
  const userToken = auth.idToken;
  const queryClient = useQueryClient();
  const setUnreadSessions = useChatNotificationsStore(
    (state) => state.setUnreadSessions,
  );

  const { data, isLoading, error, isError, refetch, isFetching } = useQuery({
    queryKey: ['chatSessions', userId],
    queryFn: () => getChatSessions(userId!, userToken!),
    enabled: !!userId,
    refetchInterval: 60 * 1000,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (data && Array.isArray(data)) {
      // Check for unread messages
      const unreadSessions = data.filter(hasUnreadMessages);
      
      // Log details of each unread session
      if (unreadSessions.length > 0) {
        console.log('🚨 DETAILS OF UNREAD SESSIONS:');
        unreadSessions.forEach((session, index) => {
          console.log(`🚨 Unread Session ${index + 1}:`, {
            id: session.id,
            user_id: session.user_id,
            last_message_sent_at: session.last_message_sent_at,
            last_read_expert: session.last_read_expert,
            created_at: session.created_at,
            updated_at: session.updated_at
          });
        });
      } else {
        
      }
      
      setUnreadSessions(unreadSessions);
    } else {
      // Clear unread sessions when no data or invalid data
      setUnreadSessions([]);
    }
  }, [data, setUnreadSessions, userId]);

  return { data, isLoading, error, isError, refetch, isFetching };
};

export default useGetChatSessions;
